import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { AbcisTableComponent, ABCisTableModel, ColumnType } from 'src/app/components/abcis-table/abcis-table.component';
import { General } from 'src/app/constants/constants';
import { ABCisApi, ApiService } from 'src/app/handlers/apiHandler.service';


@Component({
  selector: 'app-document-detail',
  standalone: false,
  templateUrl: './document-detail.component.html',
  styleUrls: ['./document-detail.component.scss'],
})
export class DocumentDetailComponent implements OnInit, OnDestroy {

  public documentID: number;
  public hasConfigured: boolean = false;
  public legacySupplierId: string;
  public showMapDialog: boolean = false;
  public showUnMapDialog: boolean = false;
  public displayIsLoading: boolean = false;
  public unMappedLineItemsTableConfig: ABCisTableModel;
  public mappedLineItemsTableConfig: ABCisTableModel;
  public fileViewValue: any;
  public selectedUnMappedLineItem: any;
  public selectedMappedLineItems: any[];
  public selectedMasterProduct: any;
  public masterProductId: any;

  private sub: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private apiHandler: ApiService,
    private toastr: ToastrService
  ) { }

  get disableMapBtn(): boolean {
    return !this.selectedUnMappedLineItem || !this.selectedMasterProduct;
  }

  get disableUnMapBtn(): boolean {
    return this.selectedMappedLineItems.length === 0;
  }

  ngOnInit() {
    this.sub = this.activatedRoute.params.subscribe(params => {
      this.documentID = +params['id'];
    });
    if (this.documentID != undefined) {
      this.getLineItems();
      this.fileViewValue = this.activatedRoute.snapshot.paramMap.get('fileUuid');
      this.legacySupplierId = localStorage.getItem(General.LEGACY_SUPPLIER_ID);
      this.selectedMappedLineItems = [];
      this.hasConfigured = true;
    } else {
      this.hasConfigured = false;
    }
  }

  ngOnDestroy() {
    localStorage.removeItem(General.LEGACY_SUPPLIER_ID);
    this.sub.unsubscribe();
  }

  setLineItemsConfig(getMapped = false) {
    // Route the users filter results. For sharing
    const config = {
      apiConfig: {
        api: ABCisApi.Ocr,
        table: 'null',
        url: `master-product/${getMapped ? 'mapped-line-items' : 'unmapped-line-items' }/${this.documentID}`,
        approxamateRowCount: 1000,
        anyParams: true
      },
      colConfig: [
        { field: 'supplierReference', header: 'Supplier Reference', type: ColumnType.String },
        { field: 'description', header: 'Description', type: ColumnType.String },
        { field: 'plu', header: 'PLU', type: ColumnType.String }
      ],
      metaKeySelection: false,
      selectionMode: getMapped ? 'multiple' : 'single',
      isConfigurable: false,
      isTable: false,
    };

    if(getMapped){
      this.mappedLineItemsTableConfig = config;
    } else{
      this.unMappedLineItemsTableConfig = config;
    }
  }

  onSelectUnMappedLineItem(value) {
    this.selectedUnMappedLineItem = this.selectedUnMappedLineItem !== undefined && this.selectedUnMappedLineItem === value.data ? undefined : value.data;
  }

  onSelectMappedLineItem(value: any) {
    const isExist = this.selectedMappedLineItems.some(e => e.documentLineItemId === value.data.documentLineItemId);
    this.selectedMappedLineItems = isExist ? this.selectedMappedLineItems.filter(e => e.documentLineItemId !== value.data.documentLineItemId) : this.selectedMappedLineItems.concat(value.data) as any;
  }

  onSelectMasterProduct(value) {
    this.selectedMasterProduct = this.selectedMasterProduct !== undefined && this.selectedMasterProduct === value.data ? undefined : value.data;
  }

  mapMasterData() {
    if (this.selectedUnMappedLineItem.documentLineItemId && this.selectedMasterProduct.productId) {
      this.showMapDialog = true;
      this.showUnMapDialog = false;
    }
  }

  unMapMasterData() {
    if(this.selectedMappedLineItems.length > 0) {
      this.showUnMapDialog = true;
      this.showMapDialog = false;
    }
  }

  confirmMapMasterData() {
    this.displayIsLoading = true;

    this.apiHandler.postABCisRequest(ABCisApi.Ocr, 'master-product/map-master-data', {
      'documentLineItemId': this.selectedUnMappedLineItem.documentLineItemId,
      'productId': this.selectedMasterProduct.productId
    }).then(() => {
      this.displayIsLoading = false;
      this.showMapDialog = false;
      this.toastr.success(`Successfully mapped (${this.selectedUnMappedLineItem.supplierReference}).`, 'Success');
      this.resetDataSelections();
      this.getLineItems();
    }).catch((error: HttpErrorResponse) => {
      this.displayIsLoading = false;
      this.toastr.error(error.error);
    });
  }

  confirmUnMapMasteData() {
    this.displayIsLoading = true;

    const requests = this.selectedMappedLineItems.map(data => {
      return this.apiHandler.postABCisRequest(ABCisApi.Ocr, 'master-product/unmap-master-data', data).then(() => {
        return { 'hasError': false, 'data': data.supplierReference };
      })
        .catch(() => {
          return { 'hasError': true, 'data': data.supplierReference };
        });
    });

    forkJoin(requests).subscribe(response => {
      const errors = response.filter(e => e.hasError).map(e => e.data);
      const success = response.filter(e => !e.hasError).map(e => e.data);

      this.toastr.success(`Successfully unmapped (${success.toString().toUpperCase()}).`, 'Success');
      if(errors.length > 0) {
        this.toastr.error(`Failed to unmap (${errors.toString().toUpperCase()}).`, 'Failed');
      }
      this.resetDataSelections();
      this.getLineItems();
      this.displayIsLoading = false;
      this.showUnMapDialog = false;
    });

  }

  getLineItems() {
    this.setLineItemsConfig(); // Fetch unmapped line items
    this.setLineItemsConfig(true); // Fetch mapped line items
  }

  resetDataSelections(){
    this.onSelectUnMappedLineItem({ data: this.selectedUnMappedLineItem });
    this.onSelectMasterProduct({ data: this.selectedMasterProduct });
    this.selectedMappedLineItems = [];
  }
}
